import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import CustomButton from '../../../components/CustomButton';
import RecurrencePatternForm from '../CreateBooking/components/SeriesBooking/RecurrencePatternForm';
import SeriesEndConditionFields from '../CreateBooking/components/SeriesBooking/SeriesEndConditionFields';
import SeriesDateList from '../CreateBooking/components/SeriesBooking/SeriesDateList';
import {
    GeneratedDate,
    RecurrencePattern,
    datesFromPreview,
    defaultRecurrencePattern,
    includedHolidaysOf,
    serverEndCondition,
    toServerPattern,
} from '../../../utils/recurrence';
import { continueSeries, createSeries, previewSeries } from '../../../flows/seriesModuleFlow';
import { SeriesContinuation } from '../../../types/bookingCreation';
import { seriesRequestFrom } from '../CreateBooking/components/SeriesBooking/seriesRequest';

// Adds occurrences to an existing series (#1376 item 1).
//
// A series created through the series module stores the rule it was created
// from (series plan §4.4.1). For those (`canContinue`) the panel asks only how
// far to extend: the server works out the rule's next dates after the series'
// last one (`POST /admin/series/:seriesId/continuation`), keeping its rhythm —
// an every-second-week series keeps its own weeks.
//
// A series without a stored rule (created before it was stored, or of
// hand-picked dates), or an admin who wants a different rhythm, restates the
// pattern instead, anchored on the day after the last occurrence so a
// regenerated date can never collide with a booking the series already has.
//
// Either way the server checks every date (series plan §4.2), and the dates
// are created into this series with `seriesId`: the create route then leaves
// the series' payment term, discount and add-on set alone and sends no mail —
// those stay with the page's own Save.

export interface SeriesExtendCustomerDetails {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    country: string;
    city: string;
    zipCode: string;
    company: string;
}

interface SeriesExtendPanelProps {
    seriesId: string;
    /** The offer the series was booked under — see #2650. */
    groupId: string | null;
    /** Date of the series' current last occurrence; new dates start after it. */
    lastOccurrenceDate: Dayjs;
    /** Time window every occurrence of this series uses (HH:mm). */
    startTime: string;
    endTime: string;
    numberOfGuests: number;
    eventType: string | null;
    comment: string;
    customerDetails: SeriesExtendCustomerDetails;
    /**
     * #1810: the series' stored attribution and cost centre, so new
     * occurrences carry the same — and are priced by the same class — as the
     * existing ones. Undefined when it could not be read: then nothing is sent.
     */
    attribution?: { organizationId: string | null, organizationUnitId: string | null, costCenter: string };
    additionalServices: { serviceId: string, name: string, quantity: number }[];
    /** The series stores a rule the server can continue (series plan §4.4.1). */
    canContinue?: boolean;
    onClose: () => void;
    onExtended: (created: number, failed: number) => void;
}

type Step = 'pattern' | 'review' | 'creating' | 'done';

const SeriesExtendPanel: React.FC<SeriesExtendPanelProps> = ({
    seriesId,
    groupId,
    lastOccurrenceDate,
    startTime,
    endTime,
    numberOfGuests,
    eventType,
    comment,
    customerDetails,
    attribution,
    additionalServices,
    canContinue = false,
    onClose,
    onExtended,
}) => {
    const { t } = useTranslation();

    // New occurrences start the day after the current last one, so the pattern
    // can never regenerate a date the series already holds.
    const anchorDate = useMemo(
        () => lastOccurrenceDate.add(1, 'day').startOf('day'),
        [lastOccurrenceDate],
    );

    // Seeded to continue the series' own rhythm: same weekday as the last
    // occurrence, one further date. Everything stays editable.
    const [pattern, setPattern] = useState<RecurrencePattern>(() => ({
        ...defaultRecurrencePattern,
        weekdays: [lastOccurrenceDate.day()],
        occurrences: 1,
    }));

    // 'rule': continue the stored rule; 'pattern': restate a pattern.
    const [mode, setMode] = useState<'rule' | 'pattern'>(canContinue ? 'rule' : 'pattern');
    const [continuation, setContinuation] = useState<SeriesContinuation | null>(null);
    const [step, setStep] = useState<Step>('pattern');
    const [dates, setDates] = useState<GeneratedDate[]>([]);
    const [createdCount, setCreatedCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [failedDates, setFailedDates] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    const includedDates = useMemo(() => dates.filter((d) => d.included), [dates]);

    // The continued rule brings its own room and times; a restated pattern
    // uses the series' current ones.
    const requestFor = (next: SeriesContinuation | null) => seriesRequestFrom(
        {
            groupId: next?.groupId ?? groupId ?? '',
            startTime: next?.startTime ?? startTime,
            endTime: next?.endTime ?? endTime,
            customerDetails,
            numberOfGuests,
            eventType,
            comment,
            additionalServices,
            attribution,
        },
        next?.pattern ?? toServerPattern(pattern, anchorDate),
    );
    const request = requestFor(mode === 'rule' ? continuation : null);

    const handleGenerate = async () => {
        setError(null);
        let next: SeriesContinuation | null = null;
        if (mode === 'rule') {
            const answer = await continueSeries(seriesId, serverEndCondition(pattern));
            if ('error' in answer) {
                setError(answer.error);
                return;
            }
            next = answer;
        }
        setContinuation(next);
        const preview = await previewSeries(requestFor(next));
        if ('error' in preview) {
            setError(preview.error);
            return;
        }
        setDates(datesFromPreview(preview.occurrences, next?.startTime ?? startTime, next?.endTime ?? endTime));
        setStep('review');
    };

    const handleCreate = async () => {
        setStep('creating');
        const response = await createSeries(
            {
                ...request,
                seriesId,
                excludedDates: dates.filter((d) => !d.included).map((d) => d.date.format('YYYY-MM-DD')),
                includedHolidays: includedHolidaysOf(dates),
            },
            // Never mails from here: a new occurrence is confirmed with the
            // series' own "Save and send", one mail for the whole series.
            'save_only',
        );
        if ('error' in response) {
            setError(response.error);
            setFailedCount(includedDates.length);
            setStep('done');
            onExtended(0, includedDates.length);
            return;
        }
        const created = response.results.filter((r) => r.outcome === 'created').length;
        const failed = response.results.filter((r) => r.outcome === 'failed');
        setCreatedCount(created);
        setFailedCount(failed.length);
        setFailedDates(failed.map((r) => r.date.split('-').reverse().join('.')));
        setStep('done');
        onExtended(created, failed.length);
    };

    // An empty weekly pattern generates nothing at all, and "until date" with no
    // date set would silently fall back to the occurrence count. Both are
    // blocked here rather than left to the preview (the server caps a series
    // at 104 dates either way).
    const isEndValid = pattern.endCondition !== 'untilDate' || !!pattern.endDate;
    const isPatternValid = mode === 'rule'
        ? isEndValid
        : (pattern.type !== 'weekly' || pattern.weekdays.length > 0) && isEndValid;
    const canCreate = includedDates.length > 0 && !!(request.groupId);

    return (
        <div className={'mt-4 rounded-lg border border-gray-200 p-4'}>
            <div className={'mb-4 flex items-center justify-between'}>
                <h2 className={'text-base font-semibold text-foreground'}>
                    {t('series_booking_details.extend.title')}
                </h2>
                {/* The rule mode continues after the last NON-canceled date
                    (server-side); this anchor counts every listed date, so it
                    would name a different day there. */}
                {mode === 'pattern' && (
                    <span className={'text-sm text-foreground/60'}>
                        {t('series_booking_details.extend.starts_after', {
                            date: lastOccurrenceDate.format('DD.MM.YYYY'),
                        })}
                    </span>
                )}
            </div>

            {step === 'pattern' && mode === 'rule' && (
                <>
                    <p className={'mb-4 text-sm text-foreground'}>
                        {t('series_booking_details.extend.continue_hint')}
                    </p>
                    <SeriesEndConditionFields
                        pattern={pattern}
                        onChange={(updates) => setPattern({ ...pattern, ...updates })}
                    />
                    <button
                        type={'button'}
                        onClick={() => { setError(null); setMode('pattern'); }}
                        className={'mt-3 text-sm text-primary underline'}
                    >
                        {t('series_booking_details.extend.other_pattern')}
                    </button>
                </>
            )}

            {step === 'pattern' && mode === 'pattern' && (
                <>
                    {canContinue && (
                        <button
                            type={'button'}
                            onClick={() => { setError(null); setMode('rule'); }}
                            className={'mb-3 text-sm text-primary underline'}
                        >
                            {t('series_booking_details.extend.follow_rule')}
                        </button>
                    )}
                    <RecurrencePatternForm pattern={pattern} onChange={setPattern} />
                </>
            )}

            {step === 'pattern' && (
                <>
                    {error && (
                        <p className={'mt-3 text-sm text-foreground'}>
                            {t('series_booking.date_review.preview_failed', { reason: error })}
                        </p>
                    )}
                    <div className={'mt-4 flex justify-end gap-2'}>
                        <CustomButton
                            title={t('common.cancel')}
                            onClick={onClose}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t('series_booking_details.extend.preview_dates')}
                            onClick={handleGenerate}
                            disabled={!isPatternValid}
                        />
                    </div>
                </>
            )}

            {step === 'review' && (
                <>
                    <SeriesDateList dates={dates} onDatesChange={setDates} />
                    <div className={'mt-4 flex justify-end gap-2'}>
                        <CustomButton
                            title={t('series_booking.buttons.back')}
                            onClick={() => setStep('pattern')}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t('series_booking_details.extend.confirm', {
                                count: includedDates.length,
                            })}
                            onClick={handleCreate}
                            disabled={!canCreate}
                        />
                    </div>
                </>
            )}

            {step === 'creating' && (
                <p className={'py-4 text-center text-sm text-foreground/60'}>
                    {t('series_booking.confirmation.creating')}
                </p>
            )}

            {step === 'done' && (
                <div className={'space-y-3'}>
                    <p className={'text-sm text-foreground'}>
                        {t('series_booking_details.extend.result', { count: createdCount })}
                    </p>
                    {error && (
                        <p className={'text-sm text-foreground'}>
                            {t('series_booking.confirmation.request_failed', { reason: error })}
                        </p>
                    )}
                    {failedCount > 0 && (
                        <p className={'text-sm text-foreground/60'}>
                            {t('series_booking_details.extend.result_failed', {
                                count: failedCount,
                            })}
                            {failedDates.length > 0 && ` — ${failedDates.join(', ')}`}
                        </p>
                    )}
                    <div className={'flex justify-end'}>
                        <CustomButton title={t('common.close')} onClick={onClose} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeriesExtendPanel;
